import parse from "html-react-parser"
import { useTranslation } from "react-i18next"

import { Sparkles } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { CourseList } from "@/features/training/components/course-list"
import {
  DemoActivityMessages,
  DemoAgentToolsBadges,
  DemoCodeActivityMessages,
  DemoDeepPhases,
  DemoDeepToggle,
} from "@/features/training/components/demo"
import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { CourseSection } from "@/features/training/components/layouts/course-section"
import { ensureArray } from "@/features/training/utils/ensure-array"

export function AIAgentsHelloAIPage() {
  const { t } = useTranslation("", { keyPrefix: "training.ai-agents.helloai" })
  const list = <T = string,>(key: string) => ensureArray<T>(t(key, { returnObjects: true }))

  return (
    <CourseLayout>
      <CourseSection>
        <h1>{t("title")}</h1>
        <h2>{t("purpose.title")}</h2>
        <p>{parse(t("purpose.description"))}</p>
        <p>
          <strong>{t("purpose.what_you_gain")}</strong>
        </p>
        <CourseList data={list("purpose.benefits")} />
        <p>
          <em>{t("purpose.level")}</em>
        </p>
      </CourseSection>

      <CourseSection>
        <h2>{t("learning_objectives.title")}</h2>
        <p>{parse(t("learning_objectives.description"))}</p>
        <CourseList data={list("learning_objectives.objectives")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("overview.title")}</h2>
        <p>{parse(t("overview.description"))}</p>
        <CourseList data={list("overview.agents")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("standard_mode.title")}</h2>
        <p>{parse(t("standard_mode.description"))}</p>
        <CourseList data={list("standard_mode.capabilities")} />

        <h3 className="mt-6">{t("standard_mode.available_tools_title")}</h3>
        <p>{parse(t("standard_mode.available_tools_description"))}</p>
        <div className="my-4">
          <DemoAgentToolsBadges />
        </div>

        <h3 className="mt-6">{t("standard_mode.activity_messages_title")}</h3>
        <p>{parse(t("standard_mode.activity_messages_intro"))}</p>
        <div className="my-4">
          <DemoActivityMessages />
        </div>
      </CourseSection>

      <CourseSection>
        <div className="flex flex-wrap items-center gap-2">
          <Sparkles className="text-primary h-6 w-6" />
          <h2 className="!mb-0">{t("deep.title")}</h2>
          <Badge variant="secondary">{t("deep.beta_badge", "Beta")}</Badge>
        </div>
        <p>{parse(t("deep.description"))}</p>

        <h3>{t("deep.how_to_enable.title")}</h3>
        <p>{parse(t("deep.how_to_enable.description"))}</p>
        <div className="my-4 flex justify-center">
          <DemoDeepToggle />
        </div>

        <h3>{t("deep.how_it_works.title")}</h3>
        <p>{parse(t("deep.how_it_works.description"))}</p>
        <div className="my-4">
          <DemoDeepPhases />
        </div>

        <h3>{t("deep.what_you_see.title")}</h3>
        <p>{parse(t("deep.what_you_see.description"))}</p>
        <CourseList data={list("deep.what_you_see.items")} />

        <h3>{t("deep.when_to_use.title")}</h3>
        <p>{parse(t("deep.when_to_use.description"))}</p>
        <CourseList data={list("deep.when_to_use.examples")} />

        <h3>{t("deep.tips.title")}</h3>
        <CourseList data={list("deep.tips.items")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("data_analysis.title")}</h2>
        <p>{parse(t("data_analysis.description"))}</p>
        <p>
          <strong>{t("data_analysis.capabilities_intro")}</strong>
        </p>
        <CourseList data={list("data_analysis.capabilities")} />
        <p className="mt-4">
          <strong>{t("data_analysis.activity_messages_intro")}</strong>
        </p>
        <div className="my-4">
          <DemoCodeActivityMessages />
        </div>
        <p className="mt-2">{t("data_analysis.seamless")}</p>
      </CourseSection>

      <CourseSection>
        <h2>{t("pitfalls.title")}</h2>
        <CourseList data={list("pitfalls.tips")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("summary.title")}</h2>
        <CourseList data={list("summary.points")} />
        <p>{parse(t("summary.conclusion"))}</p>
      </CourseSection>
    </CourseLayout>
  )
}
